import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { money, ORDER_LABELS, ORDER_COLORS } from "@/lib/format";
import { Loader } from "@/components/Common";
import {
  TrendingUp, ShoppingBag, MessageCircleWarning, CreditCard, PackageX, ArrowRight, BarChart3, Award,
} from "lucide-react";
import { AreaChart, Area, XAxis, Tooltip, ResponsiveContainer } from "recharts";

function Metric({ icon: Icon, label, value, sub, onClick, tint = "primary", testid, badge }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className="ms-card ms-lift p-5 text-left relative overflow-hidden">
      <div className="flex items-start justify-between">
        <div className={`h-10 w-10 rounded-2xl flex items-center justify-center bg-${tint}/10`}
          style={{ backgroundColor: "hsl(var(--accent))" }}>
          <Icon className="h-5 w-5 text-primary" strokeWidth={1.5} />
        </div>
        {badge > 0 && (
          <span className="text-xs font-semibold bg-red-500 text-white rounded-full px-2 py-0.5">{badge}</span>
        )}
      </div>
      <p className="mt-4 text-2xl font-head font-semibold tracking-tight">{value}</p>
      <p className="text-sm text-muted-foreground">{label}</p>
      {sub && <p className="text-xs text-muted-foreground/80 mt-1">{sub}</p>}
    </button>
  );
}

export default function Dashboard() {
  const nav = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/dashboard").then((r) => setData(r.data)).catch(() => setData(null));
  }, []);

  if (!data) return <div className="p-4 sm:p-8"><Loader /></div>;

  const estados = Object.entries(data.pedidos_por_estado || {});

  return (
    <div className="p-4 sm:p-8 max-w-6xl mx-auto">
      <div className="mb-6">
        <h1 className="font-head text-2xl sm:text-3xl font-semibold tracking-tight">Hola, buen día 💕</h1>
        <p className="text-sm text-muted-foreground mt-1">Este es el resumen de tu tienda hoy.</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Metric testid="metric-ventas-hoy" icon={TrendingUp} label="Ventas de hoy"
          value={money(data.ventas_hoy)} sub={`${data.pedidos_hoy} pedidos`} onClick={() => nav("/pedidos")} />
        <Metric testid="metric-ventas-semana" icon={ShoppingBag} label="Ventas de la semana"
          value={money(data.ventas_semana)} sub={`${data.pedidos_semana} pedidos`} onClick={() => nav("/pedidos")} />
        <Metric testid="metric-derivadas" icon={MessageCircleWarning} label="Esperando humano"
          value={data.n_derivadas} sub="conversaciones derivadas" badge={data.n_derivadas}
          onClick={() => nav("/conversaciones?vista=esperando_humano")} />
        <Metric testid="metric-pagos" icon={CreditCard} label="Pagos por validar"
          value={data.n_pagos_pendientes} sub="comprobantes pendientes" badge={data.n_pagos_pendientes}
          onClick={() => nav("/pagos")} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-4">
        {/* Derivadas destacado */}
        <div className="ms-card p-5 lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-head text-lg font-semibold flex items-center gap-2">
              <MessageCircleWarning className="h-5 w-5 text-primary" strokeWidth={1.5} /> Conversaciones esperando humano
            </h2>
            <button onClick={() => nav("/conversaciones?vista=esperando_humano")} className="text-sm text-primary flex items-center gap-1">
              Ver todas <ArrowRight className="h-4 w-4" />
            </button>
          </div>
          {data.conversaciones_derivadas.length === 0 ? (
            <p className="text-sm text-muted-foreground py-6 text-center">Todo al día, no hay conversaciones derivadas. ✨</p>
          ) : (
            <div className="space-y-2">
              {data.conversaciones_derivadas.map((c) => (
                <button key={c.id} onClick={() => nav("/conversaciones?vista=esperando_humano")}
                  className="w-full flex items-center justify-between rounded-2xl border border-border px-4 py-3 hover:bg-accent transition-colors text-left">
                  <div>
                    <p className="text-sm font-semibold">{c.contacto_nombre || "Cliente"}</p>
                    <p className="text-xs text-muted-foreground">{c.motivo_derivacion || "Requiere atención humana"}</p>
                  </div>
                  <span className="text-xs bg-amber-50 text-amber-600 rounded-full px-2.5 py-1 capitalize">{c.canal}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Pedidos por estado */}
        <div className="ms-card p-5">
          <h2 className="font-head text-lg font-semibold mb-4">Pedidos por estado</h2>
          <div className="space-y-2">
            {estados.length === 0 && <p className="text-sm text-muted-foreground">Sin pedidos aún.</p>}
            {estados.map(([est, n]) => (
              <div key={est} className="flex items-center justify-between">
                <span className={`text-xs rounded-full px-2.5 py-1 ${ORDER_COLORS[est] || "bg-gray-100"}`}>{ORDER_LABELS[est] || est}</span>
                <span className="text-sm font-semibold">{n}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Ventas 14 días + top productos */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-4">
        <div className="ms-card p-5 lg:col-span-2">
          <h2 className="font-head text-lg font-semibold mb-4 flex items-center gap-2">
            <BarChart3 className="h-5 w-5 text-primary" strokeWidth={1.5} /> Ventas de los últimos 14 días
          </h2>
          <div className="h-56 min-h-[224px]" data-testid="sales-chart">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={data.ventas_14_dias || []} margin={{ top: 5, right: 5, left: 5, bottom: 0 }}>
                <defs>
                  <linearGradient id="ventasFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <XAxis dataKey="fecha" tickFormatter={(v) => v.slice(5)} tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
                <Tooltip formatter={(v) => money(v)} labelFormatter={(v) => v} />
                <Area type="monotone" dataKey="total" stroke="hsl(var(--primary))" fill="url(#ventasFill)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="ms-card p-5">
          <h2 className="font-head text-lg font-semibold mb-4 flex items-center gap-2">
            <Award className="h-5 w-5 text-primary" strokeWidth={1.5} /> Top productos
          </h2>
          {(!data.top_productos || data.top_productos.length === 0) ? (
            <p className="text-sm text-muted-foreground py-4 text-center">Todavía no hay ventas registradas.</p>
          ) : (
            <div className="space-y-2">
              {data.top_productos.map((p, i) => (
                <div key={p.nombre} className="flex items-center justify-between text-sm">
                  <span className="truncate mr-2"><span className="text-muted-foreground mr-1">{i + 1}.</span>{p.nombre}</span>
                  <span className="font-semibold shrink-0">{p.cantidad} u.</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Stock bajo */}
      <div className="ms-card p-5 mt-4">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-head text-lg font-semibold flex items-center gap-2">
            <PackageX className="h-5 w-5 text-primary" strokeWidth={1.5} /> Productos con stock bajo
          </h2>
          <button onClick={() => nav("/productos")} className="text-sm text-primary flex items-center gap-1">
            Ver inventario <ArrowRight className="h-4 w-4" />
          </button>
        </div>
        {data.stock_bajo.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4 text-center">Todo con buen stock. 👍</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {data.stock_bajo.map((p) => (
              <div key={p.id} className="flex items-center justify-between rounded-2xl border border-border px-4 py-2.5">
                <span className="text-sm font-medium truncate mr-2">{p.nombre}</span>
                <span className={`text-xs rounded-full px-2.5 py-1 shrink-0 ${p.stock_actual === 0 ? "bg-red-50 text-red-600" : "bg-amber-50 text-amber-600"}`}>
                  {p.stock_actual === 0 ? "Sin stock" : `${p.stock_actual} u.`}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
